import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography, Paper, Tab, FormControl, Grid, Alert, CircularProgress, InputLabel, Select, MenuItem } from "@mui/material";
import { TabList, TabContext } from "@mui/lab";
import { FilterListOutlined as FilterList } from "@mui/icons-material";
import styled from "@emotion/styled";
import dayjs from "dayjs";
import PageHeader from "../components/PageHeader";
import { useHasPermission } from "../utils/permissionUtils";
import { useDispatch } from "react-redux";
import { getReportDepartments } from "../redux/ducks/reports";
import { updateClient } from "../redux/ducks/client";
import { updateAccount } from "../redux/ducks/account";
import { updateGroup } from "../redux/ducks/group";
import { downloadBlob, toCsv } from "../utils/downloadUtils";
import AppDatePicker from "../components/AppDatePicker";
import StatCard from "../components/StatCard";
import { visibleDayColumns } from "../components/RunnitDailyHours";
import { RUNNIT_REPORTS, DEFAULT_REPORT_KEY, getReport, visibleReports } from "../components/RunnitReportRegistry";

const colorToTone = (color) => {
  switch (color) {
    case 'success.main':
      return 'success';
    case 'error.main':
      return 'error';
    case 'warning.main':
      return 'warn';
    case 'info.main':
      return 'info';
    case 'primary':
      return 'info';
    default:
      return 'neutral';
  }
};

const ReportContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  boxShadow: theme.shadows[2],
  width: "100%",
  maxWidth: theme.breakpoints.values.xl,
  margin: "0 auto",
}));

const ReportActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
}));

const SummaryGrid = styled(Grid)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(1, 1fr)',
  },
}));

const StyledTabList = styled(TabList)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  minWidth: 0,
  maxWidth: '100%',
  "& .MuiTabs-flexContainer": {
    gap: theme.spacing(2),
  },
  "& .MuiTabs-scrollButtons.Mui-disabled": {
    opacity: 0.3,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  flexGrow: 1,
  [theme.breakpoints.down('sm')]: {
    flexGrow: 0,
    minWidth: 'auto',
    padding: theme.spacing(1, 1.5),
    fontSize: theme.typography.size.md,
  },
}));

const StyledFilterPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  },
}));

const FilterTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(1),
}));

const GenerateRow = styled(Grid)({
  display: 'flex',
  justifyContent: 'flex-end',
});

const InvalidRangeAlert = styled(Alert)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const InfoAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const PLACEHOLDER = "—";
const ISO = "YYYY-MM-DD";
const isoToday = (offsetMonths = 0, endOfMonth = false) => {
  const month = dayjs().add(offsetMonths, 'month');
  return (endOfMonth ? month.endOf('month') : month.startOf('month')).format(ISO);
};

const MONTH_PICKER_PROPS = {
  views: ['year', 'month'],
  openTo: 'month',
  format: 'MMM YYYY',
  localeText: { fieldMonthPlaceholder: () => 'MMM' },
};

const snapToMonth = (value, edge) => {
  const parsed = value ? dayjs(value) : null;
  if (!parsed || !parsed.isValid()) return value;
  return (edge === 'end' ? parsed.endOf('month') : parsed.startOf('month')).format(ISO);
};

const rangeBounds = ({ startDate, endDate, maxRangeDays, monthMode }) => {
  if (!maxRangeDays) return {};
  const start = startDate && dayjs(startDate).isValid() ? dayjs(startDate) : null;
  const end = endDate && dayjs(endDate).isValid() ? dayjs(endDate) : null;
  let startMin = end ? end.subtract(maxRangeDays - 1, 'day') : null;
  let endMax = start ? start.add(maxRangeDays - 1, 'day') : null;
  if (monthMode && startMin && !startMin.isSame(startMin.startOf('month'), 'day')) startMin = startMin.add(1, 'month').startOf('month');
  if (monthMode && endMax && !endMax.isSame(endMax.endOf('month'), 'day')) endMax = endMax.subtract(1, 'month').endOf('month');
  return { startMin: startMin ? startMin.format(ISO) : undefined, endMax: endMax ? endMax.format(ISO) : undefined };
};

const DEFAULT_DROPDOWNS = { clientType: "All", projectStatus: "All", department: "All" };

export default function ReportDashboard() {
  const navigate = useNavigate();
  const { tab } = useParams();
  const dispatch = useDispatch();
  const hasPermission = useHasPermission();

  const available = useMemo(() => visibleReports(hasPermission), [hasPermission]);
  const requestedKey = tab || DEFAULT_REPORT_KEY;
  const activeKey = available.some((r) => r.key === requestedKey) ? requestedKey : DEFAULT_REPORT_KEY;
  const report = getReport(activeKey);
  const dateMode = report?.filters?.dates;
  const monthMode = dateMode === 'month';
  const maxRangeDays = report?.maxRangeDays;

  const [startDate, setStartDate] = useState(isoToday());
  const [endDate, setEndDate] = useState(isoToday(0, true));
  const [dropdowns, setDropdowns] = useState(DEFAULT_DROPDOWNS);
  const [departments, setDepartments] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [failed, setFailed] = useState(false);
  const [context, setContext] = useState({});
  const [resultRange, setResultRange] = useState(null);

  useEffect(() => {
    setFilteredData([]);
    setHasFetched(false);
    setFailed(false);
    setContext({});
    setResultRange(null);
  }, [activeKey]);

  // Keyed on the mode alone: keying on the dates would rewrite them mid-keystroke.
  useEffect(() => {
    if (!monthMode) return;
    setStartDate((prev) => snapToMonth(prev, 'start'));
    setEndDate((prev) => snapToMonth(prev, 'end'));
  }, [monthMode]);

  useEffect(() => {
    dispatch(getReportDepartments((data) => {
      if (Array.isArray(data)) setDepartments(data);
    }));
  }, [dispatch]);

  const handleReportTypeChange = (event, value) => {
    navigate(`/runnit-reports/${value}`, { replace: true });
  };

  const handleReset = () => {
    setStartDate(isoToday());
    setEndDate(isoToday(0, true));
    setDropdowns(DEFAULT_DROPDOWNS);
  };

  const handleGenerateReport = () => {
    if (!report?.fetch) return;
    const range = monthMode ? { startDate: snapToMonth(startDate, 'start'), endDate: snapToMonth(endDate, 'end') } : { startDate, endDate };
    if (range.startDate !== startDate) setStartDate(range.startDate);
    if (range.endDate !== endDate) setEndDate(range.endDate);

    setLoading(true);
    setHasFetched(false);
    const onDone = (data, echoed) => {
      const succeeded = Array.isArray(data);
      setFilteredData(succeeded ? data : []);
      setResultRange(succeeded ? { startDate: (echoed && echoed.startDate) || range.startDate, endDate: (echoed && echoed.endDate) || range.endDate } : null);
      setFailed(!succeeded);
      setLoading(false);
      setHasFetched(true);
    };
    report.fetch({ dispatch, ...range, onDone, setContext });
  };

  const visibleData = useMemo(() => {
    if (!Array.isArray(filteredData)) return [];
    return report?.filterRows ? report.filterRows(filteredData, dropdowns) : filteredData;
  }, [filteredData, report, dropdowns]);

  const shownStart = hasFetched && resultRange ? resultRange.startDate : startDate;
  const shownEnd = hasFetched && resultRange ? resultRange.endDate : endDate;

  // Derived from the rows on screen, not from the range, so the table, summary and CSV agree on which
  // empty days were dropped.
  const days = useMemo(
    () => (activeKey === 'daily-hours' ? visibleDayColumns(shownStart, shownEnd, visibleData) : []),
    [activeKey, shownStart, shownEnd, visibleData]
  );

  const summaryData = useMemo(() => {
    if (!report || report.standalone) return [];
    if (!hasFetched || failed) return (report.summaryTitles || []).map((title) => ({ title, value: PLACEHOLDER }));
    return report.summary ? report.summary(visibleData, { days, context }) : [];
  }, [report, hasFetched, failed, visibleData, days, context]);

  const handleRetainerSave = useCallback(({ id, type, billingTerms }, onComplete) => {
    const updateRetainerInList = (updatedTerms) => {
      setFilteredData(prev => prev.map(r => r._id === id
        ? { ...r, billingTerms: { ...r.billingTerms, ...updatedTerms } }
        : r
      ));
    };

    if (type === 'Client') {
      dispatch(updateClient(id, { billingTerms }, (response) => {
        if (response && response.client) {
          updateRetainerInList(response.client.billingTerms);
          onComplete && onComplete(true);
        } else {
          onComplete && onComplete(false);
        }
      }));
    } else if (type === 'Account') {
      dispatch(updateAccount(id, { billingTerms }, (response) => {
        if (response && response.account) {
          updateRetainerInList(response.account.billingTerms);
          onComplete && onComplete(true);
        } else {
          onComplete && onComplete(false);
        }
      }));
    } else if (type === 'Group') {
      dispatch(updateGroup(id, { billingTerms }, (response) => {
        if (response && response.group) {
          updateRetainerInList(response.group.billingTerms);
          onComplete && onComplete(true);
        } else {
          onComplete && onComplete(false);
        }
      }));
    } else {
      onComplete && onComplete(false);
    }
  }, [dispatch]);

  const handleExport = useCallback(() => {
    if (!hasFetched || visibleData.length === 0 || !report?.csv) return;
    const stamp = `${shownStart}_to_${shownEnd}`;
    const cols = typeof report.csv.cols === 'function' ? report.csv.cols({ days }) : report.csv.cols;
    const csvRows = typeof report.csv.rows === 'function' ? report.csv.rows(visibleData) : visibleData;
    downloadBlob(toCsv(csvRows, cols), report.csv.file({ stamp, startDate: shownStart, endDate: shownEnd }));
  }, [hasFetched, visibleData, report, shownStart, shownEnd, days]);

  const handlers = useMemo(
    () => ({ onRetainerSave: handleRetainerSave, setStartDate, setEndDate }),
    [handleRetainerSave]
  );

  const dateRangeInvalid = !!(startDate && endDate && new Date(startDate) > new Date(endDate));
  const datesMissing = !!dateMode && (!startDate || !endDate);
  const unitLabel = monthMode ? "month" : "date";
  const rangeDays = useMemo(() => {
    const start = startDate ? dayjs(startDate) : null;
    const end = endDate ? dayjs(endDate) : null;
    if (!start || !start.isValid() || !end || !end.isValid()) return 0;
    return end.startOf('day').diff(start.startOf('day'), 'day') + 1;
  }, [startDate, endDate]);
  const rangeTooLong = !!maxRangeDays && rangeDays > maxRangeDays;
  const { startMin, endMax } = rangeBounds({ startDate, endDate, maxRangeDays, monthMode });
  const dropdown = report?.filters?.dropdown;
  const dropdownOptions = dropdown
    ? (dropdown.options === 'departments' ? ['All', ...departments.map((d) => d.name)] : dropdown.options)
    : [];
  const banner = report?.banner ? report.banner({ context, hasFetched, rows: visibleData, startDate: shownStart, endDate: shownEnd }) : null;

  const body = { data: visibleData, loading, hasFetched, failed, startDate: shownStart, endDate: shownEnd, handlers, context, days };

  return (
    <ReportContainer>
      <Box>
        <PageHeader
          title="Runnit Reports Dashboard"
          actions={!report?.standalone ? (
            <ReportActions>
              <Button variant="outlined" onClick={handleReset}>Reset Filters</Button>
              <Button variant="contained" onClick={handleExport} disabled={!hasFetched || visibleData.length === 0}>Export Report</Button>
            </ReportActions>
          ) : null}
        />

        <TabContext value={activeKey}>
          <StyledTabList onChange={handleReportTypeChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            {available.map((r) => (
              <StyledTab key={r.key} icon={r.icon} iconPosition="start" value={r.key} label={r.label} />
            ))}
          </StyledTabList>

          {report?.standalone ? (
            report.render(body)
          ) : (
          <>
          <SummaryGrid>
            {summaryData.map((item, index) => (
              <StatCard key={index} size="large" tone={colorToTone(item.color)} label={item.title} value={item.value} sub={item.subtext} />
            ))}
          </SummaryGrid>

          <StyledFilterPaper>
            <FilterTitle variant="h6">
              <FilterList fontSize="small" />
              Filter Options
            </FilterTitle>

            <Grid container spacing={3}>
              {dateMode && (
                <>
                  <Grid item xs={12} md={4}>
                    {/* Keyed by mode: swapping format and views under a mounted picker crashes it
                        when a section that mode no longer has is still focused. */}
                    <AppDatePicker
                      key={`start-${dateMode}`}
                      label={monthMode ? "Start Month" : "Start Date"}
                      value={startDate}
                      onChange={(v) => setStartDate(monthMode ? snapToMonth(v, 'start') : v)}
                      minDate={startMin}
                      maxDate={endDate || undefined}
                      error={dateRangeInvalid || rangeTooLong || undefined}
                      helperText={monthMode ? "Report starts on the 1st of this month" : "First day included in the report"}
                      {...(monthMode ? MONTH_PICKER_PROPS : {})}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <AppDatePicker
                      key={`end-${dateMode}`}
                      label={monthMode ? "End Month" : "End Date"}
                      value={endDate}
                      onChange={(v) => setEndDate(monthMode ? snapToMonth(v, 'end') : v)}
                      minDate={startDate || undefined}
                      maxDate={endMax}
                      error={dateRangeInvalid || rangeTooLong || undefined}
                      helperText={monthMode ? "The whole month is included" : "Last day included in the report"}
                      {...(monthMode ? MONTH_PICKER_PROPS : {})}
                    />
                  </Grid>
                </>
              )}

              {dropdown && (
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id={`${dropdown.name}-label`}>{dropdown.label}</InputLabel>
                    <Select
                      labelId={`${dropdown.name}-label`}
                      id={dropdown.name}
                      value={dropdowns[dropdown.name]}
                      label={dropdown.label}
                      onChange={(e) => setDropdowns((prev) => ({ ...prev, [dropdown.name]: e.target.value }))}
                    >
                      {dropdownOptions.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <GenerateRow item xs={12}>
                <FormControl>
                  <Button variant="contained" onClick={handleGenerateReport} disabled={loading || dateRangeInvalid || datesMissing || rangeTooLong}>
                    {loading ? <><CircularProgress size={20} /> Generating...</> : "Generate Report"}
                  </Button>
                </FormControl>
              </GenerateRow>
            </Grid>
            {dateRangeInvalid && <InvalidRangeAlert severity="error">{`Start ${unitLabel} cannot be after end ${unitLabel}`}</InvalidRangeAlert>}
            {!dateRangeInvalid && datesMissing && (
              <InvalidRangeAlert severity="warning">{`Pick a start ${unitLabel} and an end ${unitLabel} to generate this report`}</InvalidRangeAlert>
            )}
            {!dateRangeInvalid && !datesMissing && rangeTooLong && (
              <InvalidRangeAlert severity="warning">
                {`This report covers at most ${maxRangeDays} days at a time and the selected range is ${rangeDays} days. Pick a later start ${unitLabel} or an earlier end ${unitLabel} to generate it.`}
              </InvalidRangeAlert>
            )}
          </StyledFilterPaper>

          {hasFetched && banner && <InfoAlert severity="info">{banner}</InfoAlert>}

          {report?.render(body)}
          </>
          )}
        </TabContext>
      </Box>
    </ReportContainer>
  );
}
